/** Primary map-purpose switch: habitat context or maintenance planning. */

import { memo } from "react";
import type { ReactNode } from "react";

import { useLanguageContext } from "../hooks/useLanguage";
import type { MapMode } from "../types";

interface MapModeSwitchProps {
  mode: MapMode;
  onChange: (mode: MapMode) => void;
  onMunicipalityClear: () => void;
  onExport: () => void;
  selectedMunicipality: string;
  warning?: ReactNode;
}

export const MapModeSwitch = memo(function MapModeSwitch({
  mode,
  onChange,
  onMunicipalityClear,
  onExport,
  selectedMunicipality,
  warning,
}: MapModeSwitchProps) {
  const { t } = useLanguageContext();
  const renderFocusAreaBadge = () => (
    <>
      <p className="font-utility text-[0.62rem] uppercase tracking-[0.14em] text-flima-slate/55">
        {t("focus_area")}
      </p>
      <p className="truncate text-sm font-semibold text-flima-spruce">
        {selectedMunicipality}
      </p>
    </>
  );

  return (
    <nav
      data-tour-id="map-mode-switch"
      className="relative z-20 grid shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 border-b border-flima-spruce/10 bg-white px-2 py-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:px-3"
      aria-label={t("map_mode")}
    >
      <div className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          onClick={onMunicipalityClear}
          className="flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-flima-mist p-1 px-3 text-xs font-semibold text-flima-slate/70 transition-colors hover:bg-white hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none sm:text-sm"
          aria-label={`${t("change_municipality")}: ${selectedMunicipality}`}
          title={t("change_municipality")}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <path d="M9 22V12h6v10" />
          </svg>
          <span className="hidden sm:inline">{t("change_municipality")}</span>
        </button>
        <div className="ml-4 hidden min-w-0 sm:block xl:hidden">
          {renderFocusAreaBadge()}
        </div>
      </div>
      <div className="pointer-events-none absolute left-80 top-1/2 hidden max-w-[12rem] -translate-y-1/2 xl:block">
        {renderFocusAreaBadge()}
      </div>
      <div className="col-span-3 row-start-2 w-full justify-self-stretch sm:col-auto sm:row-auto sm:w-auto sm:justify-self-center">
        <div
          data-tour-id="mode-toggle-group"
          className="grid w-full grid-cols-2 rounded-lg bg-flima-mist p-1 sm:flex sm:w-auto"
          role="group"
        >
          <button
            type="button"
            data-tour-id="habitat-mode-button"
            onClick={() => onChange("habitats")}
            aria-pressed={mode === "habitats"}
            className={`min-h-11 rounded-md px-2 text-xs font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none min-[380px]:px-3 sm:whitespace-nowrap sm:px-3 sm:text-sm ${
              mode === "habitats"
                ? "bg-flima-spruce text-white shadow-sm"
                : "text-flima-slate/70 hover:bg-white hover:text-flima-spruce"
            }`}
          >
            {t("habitat_mode")}
          </button>
          <button
            type="button"
            data-tour-id="planning-mode-button"
            onClick={() => onChange("planning")}
            aria-pressed={mode === "planning"}
            className={`min-h-11 rounded-md px-2 text-xs font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none min-[380px]:px-3 sm:whitespace-nowrap sm:px-3 sm:text-sm ${
              mode === "planning"
                ? "bg-flima-spruce text-white shadow-sm"
                : "text-flima-slate/70 hover:bg-white hover:text-flima-spruce"
            }`}
          >
            {t("planning_mode")}
          </button>
        </div>
      </div>
      {/* Right zone (col 3): the warning and export share this cell so the mode
          toggle stays centred between two equal 1fr columns. The warning shrinks
          and truncates to a single line; export always keeps its full width. */}
      <div className="flex min-w-0 items-center justify-end gap-2">
        {warning ? (
          <div className="hidden min-w-0 lg:block">{warning}</div>
        ) : null}
        <button
          type="button"
          data-tour-id="export-button"
          onClick={onExport}
          className="flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-flima-mist p-1 px-3 text-xs font-semibold text-flima-slate/70 transition-colors hover:bg-white hover:text-flima-spruce focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-2 motion-reduce:transition-none sm:text-sm"
          aria-label={t("export_pdf")}
          title={t("export_pdf")}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="hidden sm:inline">{t("export_pdf")}</span>
        </button>
      </div>
    </nav>
  );
});
